<template>

	<template v-if="!isAuthenticated">
		<LoginSkin />
		<Login />
	</template>

	<template v-if="isAuthenticated && canAccessRoute">
		<div :class="containerClass" @click="onWrapperClick">
			<AppTopBar @menu-toggle="onMenuToggle"
					   @logout="logout"
					   @refreshApp="refreshApp"
					   :email="email"
					   />

			<div class="layout-sidebar" @click="onSidebarClick" v-if="isInternalUser">
				<AppMenu :model="menu" @menuitem-click="onMenuItemClick" />
				<br />
				<Button label="Logout" @click="logout" />
				<div style="position:absolute;bottom:5px;">
					<small>{{this.$store.getters.localStorageSize}}</small>
				</div>
			</div>

			<div class="layout-main-container">
				<div class="flex-grow">
					<router-view v-slot="{Component}">
						<keep-alive include="CustomerList,SupplierList,OwnerList,AuthorityList,AllCompanyList,Dashboard,PropertyList,MailingList">
							<component :is="Component" />
						</keep-alive>
					</router-view>
				</div>
				<AppFooter />
			</div>
			<ConfirmDialog />
		</div>
	</template>
	
	<!-- Root level Toast component -->
	<Toast />
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { DecodeToken } from '@/store/JwtToken';
	import AppTopBar from './ui/AppTopbar.vue';
	import AppMenu from './ui/AppMenu.vue';
	import AppFooter from './ui/AppFooter.vue';
	import getMenu from './menu-tree';
	import Toast from '@/utils/Toast';

	@Options({
		props: {
			companyId: 0
		},
		components: {
			'AppTopBar': AppTopBar,
			'AppMenu': AppMenu,
			'AppFooter': AppFooter,
		},
	})
	export default class MyApp extends Vue {

		layoutMode = 'static';
		menuClick = false;
		staticMenuInactive = false;
		overlayMenuActive = false;
		mobileMenuActive = false;

		// Vue application instantiation (created) event, start a timer in the vuex store for any time based reactivity
		created() {
			window.setInterval(() => { this.$store.dispatch('updateTicks') }, 1000);

			// Set Toast main app component
			Toast.mainAppComponent = this;
		}

		// Computed (alias to the central isauthenticated value)
		get isAuthenticated(): boolean {
			return this.$store.getters.isAuthenticated;
		}

		get isInternalUser(): boolean {
			return this.$store.getters.isInternalUser;
		}

		get canAccessRoute(): boolean {
			const meta = this.$router.currentRoute.value.meta;
			if (typeof (meta.authorize) === 'function')
				return (meta.authorize() == true);
			else
				return false;
		}

		get menu() {
			const m = getMenu();
			return m;
		}

		// For debug
		get currentToken(): string {
			return JSON.stringify(DecodeToken(this.$store.state.jwtToken), null, 1)
				+ "\nroles: " + this.$store.getters.jwtRoles;
		}

		// Computed isDesktop
		get isDesktop() {
			return window.innerWidth >= 992;
		}

		// Computed container class
		get containerClass() {
			return ['layout-wrapper', {
				'layout-overlay': this.layoutMode === 'overlay',
				'layout-static': this.isInternalUser && this.layoutMode === 'static',
				'layout-static-sidebar-inactive': this.staticMenuInactive && this.layoutMode === 'static',
				'layout-overlay-sidebar-active': this.overlayMenuActive && this.layoutMode === 'overlay',
				'layout-mobile-sidebar-active': this.mobileMenuActive,
				'p-input-filled': true,
				'p-ripple-disabled': false,
				'layout-theme-light': true
			}];
		}

		// Action - log out
		logout() {
			this.$store.dispatch('logout');
		}

		// Action - handle 'reload' menu item in toolbar
		refreshApp() {
			this.$store.dispatch('refreshApp');
		}

		// Action - menu toggle
		onMenuToggle(event: Event) {
			this.menuClick = true;

			if (this.isDesktop) {
				if (this.layoutMode === 'overlay') {
					if (this.mobileMenuActive === true) {
						this.overlayMenuActive = true;
					}

					this.overlayMenuActive = !this.overlayMenuActive;
					this.mobileMenuActive = false;
				}
				else if (this.layoutMode === 'static') {
					this.staticMenuInactive = !this.staticMenuInactive;
				}
			}
			else {
				this.mobileMenuActive = !this.mobileMenuActive;
			}
			event.preventDefault();
		}

		onMenuItemClick(event: MenuTreeEvent) {
			// used to cancel active mobile menu
			if (event.item && !event.item.items) {
				this.overlayMenuActive = false;
				this.mobileMenuActive = false;
			}
		}

		onWrapperClick() {
			// used to cancel active mobile menu
			if (!this.menuClick) {
				this.overlayMenuActive = false;
				this.mobileMenuActive = false;
			}
			this.menuClick = false;
		}

		get email() {
			return this.$store.state.email;
		}

	}
</script>

<style lang="scss">
	#app {
		font-family: Inter, Avenir, Helvetica, Arial, sans-serif;
		font-size: 14px;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
		color: #2c3e50;
	}

	#nav {
		padding: 15px;

		a {
			font-weight: bold;
			color: #2c3e50;

			&.router-link-exact-active {
				color: #fff;
			}
		}
	}
</style>
